import { useCallback, useEffect, useRef, useState } from "react";
import { getMonthlyPerformance } from "../../../services/clearing/station-performance";
import type { Baseline, ReportingOptions } from "../../../types/stationPerformance";

interface UseUnitReconciliationArgs {
  /** Falsy skips the fetch. */
  month: string | null;
  options: ReportingOptions;
}

export interface UnitReconciliation {
  perSite: Baseline;
  perConnector: Baseline;
  activeSites: number;
  activeConnectors: number;
  /** activeConnectors / activeSites: the factor between the two per-unit figures. */
  connectorsPerSite: number | null;
  /** perSiteMoney / perConnectorMoney. Should equal connectorsPerSite. */
  observedRatio: number | null;
  /** True when the two agree to within a rounding cent's worth of relative error. */
  reconciles: boolean;
}

/**
 * Fetches the same month on both reporting units so the two per-unit figures can be shown together
 * (CP-388 deliverable 1).
 *
 * Two calls rather than one endpoint returning both: the API is a single-unit view by design, and asking
 * it twice is cheap since both reads are served from the same precomputed rows.
 *
 * Ignores the caller's `unit`, deliberately: the whole point is to show both regardless of which one the
 * reader is currently looking at.
 */
export const useUnitReconciliation = ({ month, options }: UseUnitReconciliationArgs) => {
  const [reconciliation, setReconciliation] = useState<UnitReconciliation | null>(null);
  const [loading, setLoading] = useState(false);

  // Month and options can change while requests are in flight: only the newest load may write state.
  const requestId = useRef(0);

  const fetchReconciliation = useCallback(async () => {
    const id = ++requestId.current;
    const isStale = () => id !== requestId.current;
    if (!month) {
      // No month, nothing to show: never keep the previous month's figures.
      setReconciliation(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    let site, connector;
    try {
      [site, connector] = await Promise.all([
        getMonthlyPerformance({ month, options: { ...options, unit: "SITE" }, limit: 1 }),
        getMonthlyPerformance({ month, options: { ...options, unit: "CONNECTOR" }, limit: 1 }),
      ]);
    } catch {
      if (isStale()) return;
      setReconciliation(null);
      setLoading(false);
      return;
    }
    if (isStale()) return;
    setLoading(false);

    const perSite: Baseline | undefined = site?.data?.networkAverage;
    const perConnector: Baseline | undefined = connector?.data?.networkAverage;
    if (!site?.success || !connector?.success || !perSite || !perConnector) {
      setReconciliation(null);
      return;
    }

    const activeSites = perSite.basisCount ?? 0;
    const activeConnectors = perConnector.basisCount ?? 0;
    const siteMoney = perSite.moneyCents?.value ?? null;
    const connectorMoney = perConnector.moneyCents?.value ?? null;

    const connectorsPerSite = activeSites > 0 ? activeConnectors / activeSites : null;
    const observedRatio =
      siteMoney !== null && connectorMoney !== null && connectorMoney !== 0
        ? siteMoney / connectorMoney
        : null;

    // Both figures are rounded to the cent server-side, so exact equality is the wrong test; a 0.5%
    // relative tolerance is well inside rounding and well outside any real disagreement.
    const reconciles =
      connectorsPerSite !== null &&
      observedRatio !== null &&
      Math.abs(observedRatio - connectorsPerSite) / connectorsPerSite <= 0.005;

    setReconciliation({
      perSite,
      perConnector,
      activeSites,
      activeConnectors,
      connectorsPerSite,
      observedRatio,
      reconciles,
    });
  }, [month, options]);

  useEffect(() => {
    fetchReconciliation();
    return () => {
      requestId.current += 1;
    };
  }, [fetchReconciliation]);

  return { reconciliation, loading, fetchReconciliation };
};
